import { Button, Form, Input, Modal, message } from 'antd';
import React, { FunctionComponent } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForceChangeUserPasswordMutation } from '@services/data-access';

export interface ChangePasswordPopupProps {
  userId: string;
  visible: boolean;
  onClose: () => void;
}

export interface ChangePasswordFormValues {
  password: string;
  reTypePassword: string;
}

export const ChangePasswordPopup: FunctionComponent<
  ChangePasswordPopupProps
> = ({ userId, visible, onClose }) => {
  const [form] = Form.useForm();

  const [forceChangeUserPasswordMutation, { loading }] =
    useForceChangeUserPasswordMutation({
      onError: () => {},
      onCompleted: () => {
        message.success('Password changed successfully.');
        closePopup();
      },
    });

  const submitForm = (value: ChangePasswordFormValues) => {
    forceChangeUserPasswordMutation({
      variables: {
        form: {
          id: userId,
          password: value.password,
        },
      },
    });
  };

  const resetForm = () => {
    form.resetFields();
  };

  const closePopup = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      forceRender
      centered
      title="Change Password"
      visible={visible}
      onOk={closePopup}
      onCancel={closePopup}
      footer={[
        <Button
          className="fixed-width-btn button-black-text"
          key="back"
          onClick={closePopup}
        >
          Cancel
        </Button>,
        <Button
          loading={loading}
          className="fixed-width-btn"
          key="submit"
          type="primary"
          onClick={() => {
            form
              .validateFields()
              .then(values => {
                submitForm(values as ChangePasswordFormValues);
              })
              .catch(info => {
                console.log('Validate Failed:', info);
              });
          }}
        >
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="New Password"
          name="password"
          rules={[
            {
              required: true,
              message: 'Please fill in new password!',
            },
            {
              min: 8,
              message: 'Pleas fill in at least 8 characters!',
            },
          ]}
        >
          <Input.Password
            placeholder="Fill in your new password"
            prefix={<FontAwesomeIcon icon={['far', 'lock']} />}
          />
        </Form.Item>
        <Form.Item
          label="Re-Type New Password"
          name="reTypePassword"
          rules={[
            {
              required: true,
              message: 'Please fill in re-type password!',
            },
            {
              min: 8,
              message: 'Pleas fill in at least 8 characters!',
            },
            ({ getFieldValue }) => ({
              validator(rule, value) {
                if (!value) {
                  return Promise.reject();
                }
                if (value.length > 7 && getFieldValue('password') !== value) {
                  return Promise.reject(
                    'Re-type password must be match with password.',
                  );
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Re-type new password"
            prefix={<FontAwesomeIcon icon={['far', 'lock']} />}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
